import { Temporal } from '@js-temporal/polyfill';
import type { DataValue } from '@glueapp/graph-types';

// ── Plain object → Temporal ──────────────────────────────────────────

export function toPlainDate(value: { year: number; month: number; day: number }): Temporal.PlainDate {
    return Temporal.PlainDate.from({ year: value.year, month: value.month, day: value.day });
}

export function toPlainTime(value: { hour: number; minute: number; second: number; millisecond: number }): Temporal.PlainTime {
    return Temporal.PlainTime.from({
        hour: value.hour,
        minute: value.minute,
        second: value.second,
        millisecond: value.millisecond,
    });
}

export function toPlainDateTime(value: {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
}): Temporal.PlainDateTime {
    return Temporal.PlainDateTime.from({
        year: value.year,
        month: value.month,
        day: value.day,
        hour: value.hour,
        minute: value.minute,
        second: value.second,
        millisecond: value.millisecond,
    });
}

export function toZonedDateTime(value: {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
    timeZone: string;
}): Temporal.ZonedDateTime {
    return Temporal.PlainDateTime.from({
        year: value.year,
        month: value.month,
        day: value.day,
        hour: value.hour,
        minute: value.minute,
        second: value.second,
        millisecond: value.millisecond,
    }).toZonedDateTime(value.timeZone);
}

export function toDuration(value: {
    years: number;
    months: number;
    weeks: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
}): Temporal.Duration {
    return Temporal.Duration.from({
        years: value.years,
        months: value.months,
        weeks: value.weeks,
        days: value.days,
        hours: value.hours,
        minutes: value.minutes,
        seconds: value.seconds,
        milliseconds: value.milliseconds,
    });
}

export function toInstant(value: { epochMilliseconds: number }): Temporal.Instant {
    return Temporal.Instant.fromEpochMilliseconds(value.epochMilliseconds);
}

// ── Temporal → Plain object ──────────────────────────────────────────

export function fromPlainDate(date: Temporal.PlainDate): { year: number; month: number; day: number } {
    return { year: date.year, month: date.month, day: date.day };
}

export function fromPlainTime(time: Temporal.PlainTime): {
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
} {
    return { hour: time.hour, minute: time.minute, second: time.second, millisecond: time.millisecond };
}

export function fromPlainDateTime(dateTime: Temporal.PlainDateTime): {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
} {
    return {
        year: dateTime.year,
        month: dateTime.month,
        day: dateTime.day,
        hour: dateTime.hour,
        minute: dateTime.minute,
        second: dateTime.second,
        millisecond: dateTime.millisecond,
    };
}

export function fromZonedDateTime(zdt: Temporal.ZonedDateTime): {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
    timeZone: string;
    offset: string;
} {
    return {
        year: zdt.year,
        month: zdt.month,
        day: zdt.day,
        hour: zdt.hour,
        minute: zdt.minute,
        second: zdt.second,
        millisecond: zdt.millisecond,
        timeZone: zdt.timeZoneId,
        offset: zdt.offset,
    };
}

export function fromDuration(duration: Temporal.Duration): {
    years: number;
    months: number;
    weeks: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
} {
    return {
        years: duration.years,
        months: duration.months,
        weeks: duration.weeks,
        days: duration.days,
        hours: duration.hours,
        minutes: duration.minutes,
        seconds: duration.seconds,
        milliseconds: duration.milliseconds,
    };
}

export function fromInstant(instant: Temporal.Instant): { epochMilliseconds: number } {
    return { epochMilliseconds: instant.epochMilliseconds };
}

// ── Auto-detect and convert ──────────────────────────────────────────

type TemporalLike = Temporal.PlainDate | Temporal.PlainTime | Temporal.PlainDateTime | Temporal.ZonedDateTime;

export function objectToTemporal(value: Record<string, unknown>): TemporalLike {
    if ('timeZone' in value) {
        return toZonedDateTime(
            value as {
                year: number;
                month: number;
                day: number;
                hour: number;
                minute: number;
                second: number;
                millisecond: number;
                timeZone: string;
            },
        );
    }
    if ('year' in value && 'hour' in value) {
        return toPlainDateTime(
            value as {
                year: number;
                month: number;
                day: number;
                hour: number;
                minute: number;
                second: number;
                millisecond: number;
            },
        );
    }
    if ('year' in value) {
        return toPlainDate(value as { year: number; month: number; day: number });
    }
    return toPlainTime(value as { hour: number; minute: number; second: number; millisecond: number });
}

export function temporalToObject(temporal: TemporalLike): Record<string, DataValue> {
    if (temporal instanceof Temporal.ZonedDateTime) {
        return fromZonedDateTime(temporal);
    }
    if (temporal instanceof Temporal.PlainDateTime) {
        return fromPlainDateTime(temporal);
    }
    if (temporal instanceof Temporal.PlainDate) {
        return fromPlainDate(temporal);
    }
    return fromPlainTime(temporal as Temporal.PlainTime);
}
